import type { Component } from '../../router';
import { getLocale } from '../../i18n/locale';
import type { Locale } from '../../i18n/catalog';

// SiteFooter — the storefront footer, mounted by the AppShell beneath the main
// content column. Its job in GDPR-005 is a clear legal nav linking the four public
// legal documents; the copy for those lives in each page's own lazy chunk, and
// only the short link labels live here (so the footer stays tiny in the main
// bundle). A later effort (GDPR-006 / #40) adds an FDA health-claims disclaimer
// line — this footer is kept minimal and cohesive so that slots in cleanly.
//
// Labels are localized for EN/ES/PT; the whole shell re-mounts on a language
// change (LanguageSwitcher → rerender), so the footer re-reads the active locale.

interface FooterStrings {
  nav: string;
  privacy: string;
  cookies: string;
  terms: string;
  shipping: string;
  tagline: string;
}

const STRINGS: Record<Locale, FooterStrings> = {
  en: {
    nav: 'Legal',
    privacy: 'Privacy Policy',
    cookies: 'Cookie Policy',
    terms: 'Terms of Sale',
    shipping: 'Shipping & Returns',
    tagline: 'Dietary supplements. Made in USA.',
  },
  es: {
    nav: 'Legal',
    privacy: 'Política de Privacidad',
    cookies: 'Política de Cookies',
    terms: 'Términos de Venta',
    shipping: 'Envíos y Devoluciones',
    tagline: 'Complementos alimenticios. Hecho en EE. UU.',
  },
  pt: {
    nav: 'Jurídico',
    privacy: 'Política de Privacidade',
    cookies: 'Política de Cookies',
    terms: 'Termos de Venda',
    shipping: 'Envios e Devoluções',
    tagline: 'Suplementos alimentares. Fabricado nos EUA.',
  },
};

const LINKS: ReadonlyArray<readonly [keyof FooterStrings, string]> = [
  ['privacy', '/legal/privacy'],
  ['cookies', '/legal/cookies'],
  ['terms', '/legal/terms'],
  ['shipping', '/legal/shipping-returns'],
];

export class SiteFooter implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const s = STRINGS[getLocale()] ?? STRINGS.en;

    const footer = document.createElement('footer');
    footer.className = 'site-footer';

    const inner = document.createElement('div');
    inner.className = 'site-footer__inner';

    const nav = document.createElement('nav');
    nav.className = 'site-footer__legal';
    nav.setAttribute('aria-label', s.nav);
    for (const [key, href] of LINKS) {
      const a = document.createElement('a');
      a.className = 'site-footer__link';
      a.href = href;
      a.textContent = s[key];
      if (location.pathname === href) a.setAttribute('aria-current', 'page');
      nav.appendChild(a);
    }

    const meta = document.createElement('p');
    meta.className = 'site-footer__meta';
    meta.textContent = `© ${new Date().getFullYear()} Asmaluz · ${s.tagline}`;

    inner.append(nav, meta);
    footer.appendChild(inner);
    host.appendChild(footer);
    this.root = footer;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
